/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'jquery',
	'underscore',
	'../DynamicFileLoader',
	'../lib/@waca/core-client/js/core-client/ui/core/View' //TODO change to non lib version
], function($, _, DynamicFileLoader, View) {

	/**
	 * Creates an application bar to serve as a main menu bar
	 */
	var Toolbar = View.extend({
		moduleMap: [],
		addItemsReady: null,

		init: function(options) {
			Toolbar.inherited('init', this, arguments);

			this.options = (!this.options) ? {} : this.options;
			_.extend(this.options, options);

			this.items = options.items;
			this.itemMap = {};
			this._readyArray = [];
			this._reactToolbarContainer = null;
			this._isReactToolbar = this._canRenderInReactToolbar(options.items);

			this._activeFlyoutView = null;
		},

		/**
		 * Adds an item to the toolbar
		 *
		 * Returns a deferred object that will be resolve once the rendered item is added to the toolbar
		 */
		addItem: function(itemSpec) {
			return this._addItem(itemSpec);
		},

		_addItem: function(itemSpec) {
			// Need to add the item to the bar before requiring a module
			var $item = $('<div>');
			this.$el.append($item);
			return this._renderItem(itemSpec, $item);
		},

		_renderItem: function(itemSpec, $item) {
			if (!itemSpec.type && !itemSpec.module) {
				itemSpec.type = 'Menu';
			}

			var module = itemSpec.type ? ('ui/toolbar_components/' + itemSpec.type) : itemSpec.module;
			return this._renderItemHelper(module, itemSpec, $item);
		},

		_renderItemHelper: function(module, itemSpec, $item) {
			const promise = DynamicFileLoader.load([module])
				.then((modules) => {
					const Item = modules[0];
					itemSpec.el = $item;
					const item = new Item(itemSpec, this.rootMenu || this.parentView, this);
					this.itemMap[item.name] = item;
					return Promise.resolve().then(item.render.bind(item));
				});
			this._readyArray.push(promise);
			return promise;
		},

		/**
		 * Removes an item from the toolbar
		 */
		removeItem: function(itemName) {
			this.itemMap[itemName].remove();
			delete this.itemMap[itemName];
		},

		/**
		 * If any item is of type SubView, we want to create the toolbar in the old way.
		 * React toolbar does not support SubViews
		 */
		_canRenderInReactToolbar(itemSpecs = []) {
			const isSupportedInReactToolbar = (spec) => {
				return spec.type !== 'SubView';
			};

			return this.options.reactToolbar && itemSpecs.every(isSupportedInReactToolbar);
		},

		/**
		 * Add an array of items to the toolbar
		 * @param itemSpecs
		 *
		 * Returns a deferred object that will be resolved when all items are rendered and added to the toolbar
		 */
		addItems: function(itemSpecs = []) {
			this._isReactToolbar = this._canRenderInReactToolbar(itemSpecs);
			if (this._isReactToolbar) {
				this.addItemsReady = DynamicFileLoader.load(['ui/react_toolbar/ReactToolbarContainer'])
					.then((modules) => {
						const ReactToolbarContainer = modules[0];
						this._reactToolbarContainer = new ReactToolbarContainer(itemSpecs, this.el, this);
						this.itemMap = itemSpecs;  // TODO: consumers are expecting an item name:view map and not an array of specs
					});
			} else {
				this.addItemsReady = Promise.all(
					itemSpecs.map((itemSpec) => {
						return this._addItem(itemSpec);
					})
				);
			}

			return this.addItemsReady;
		},

		/**
		 * @description A tool on the toolbar can be associated with a FlyoutView which opens when the tool is clicked.
		 * (usually a button of type NextView).  This function manages the lifecycle of the flyoutSubView as well as
		 * OPEN/CLOSE rules for Toggle-Type flyouts:
		 * - consecutive clicks of the same button should open/close the flyout,
		 * - any case where the child popup closes should set the activeFlyoutView to null (ie: close it)
		 * 		(this includes clicking a different tool, clicking away, escape, executing some actions)
		 *
		 * The state is maintained outside of the tools themselves because in some cases, a toolbar may re-render when
		 * the popup is open (eg Choosing a visType rerenders the toolbar but should NOT close the popup)
		 * @param {} activeFlyoutView
		 */
		setActiveFlyoutView(activeFlyoutView) {
			if (this._activeFlyoutView && activeFlyoutView!==this._activeFlyoutView) {
				//If we had a different activeFlyoutView, remove it.
				this._activeFlyoutView.remove && this._activeFlyoutView.remove();
			}
			//Set the active activeFlyoutView to the new one.
			this._activeFlyoutView = activeFlyoutView;
		},

		getActiveFlyoutViewName() {
			return this._activeFlyoutView && this._activeFlyoutView.name;
		},

		clearItems: function() {
			_.each(this.itemMap, (item) => {
				if (item.remove) {
					item.remove();
				}
			});
			if (this._isReactToolbar) {
				if (this._reactToolbarContainer) {
					this._reactToolbarContainer.clearItems();
					this._reactToolbarContainer = null;
				}
			} else {
				this.$el.empty();
			}
			this.itemMap = {};
			this._readyArray = [];
		},

		/**
		 * Draws the Toolbar
		 */
		render: function() {
			this.$el.empty();
			if (this.items) {
				return this.addItems(this.items);
			}
			return Promise.resolve();
		},

		ready: function() {
			if (this._isReactToolbar) {
				return this.addItemsReady.then(() => {
					let orientation = (this.options.preferredVertical) ? 'vertical' : 'horizontal';
					orientation = (this.placement === 'top') ? 'horizontal' : orientation;
					return this._reactToolbarContainer.ready(orientation);
				});
			} else {
				return Promise.all(this._readyArray);
			}
		},

		setFocus: function() {
			var first = Object.keys(this.itemMap)[0];
			if (first) {
				var focusElement = this.itemMap[first];
				if (focusElement && focusElement.setFocus) {
					focusElement.setFocus();
				}
			}
		},

		updateButtons: function() {
			if (this.itemMap) {
				_.each(this.itemMap, (item) => {
					if (item.update) {
						item.render();
					}
				});
			}
		},

		remove () {
			this.clearItems();
			Toolbar.inherited('remove', this, arguments);
		}
	});

	return Toolbar;
});
